<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import {
  TSDataTable,
  TSDialogReportDamage,
  TSDialogReportMissing,
  getTransactionRole,
} from 'tsv2-library';
import { FetchResponse } from 'tsv2-library/dist/src/components/v2/DataTable/DataTable.vue';
import { MenuOption } from 'tsv2-library/dist/src/components/v2/Menu/Menu.vue';

import BorrowingLog from '@/components/common/Dialog/BorrowingLog.vue';
import {
  childColumns,
  detailColumns,
} from '@/components/common/options/columns';
import {
  borrowDetailChildrenOptions,
  borrowDetailOptions,
} from '@/components/common/options/menus';
import CancelBorrowingTransaction from './Dialog/CancelBorrowingTransaction.vue';
import CancelExtensionRequest from './Dialog/CancelExtensionRequest.vue';
import DeclineExtensionRequest from './Dialog/DeclineExtensionRequest.vue';

import useTableStore from '@/store/table.store';

import AssetConfirmation from '@/components/common/Dialog/AssetConfirmation.vue';
import CancelReport from '@/components/common/Dialog/CancelReport.vue';
import { BorrowingTransactionRequest } from '@/types/borrowing.type';
import {
  BorrowingRequestFetchParams,
  BorrowingRequestFilterParams,
} from '@/types/params.type';

type ExtendedMenuOption = MenuOption & { for?: string[] };

const router = useRouter();
const { tableKey, incrementTableKey } = useTableStore();

const selectedData = defineModel<BorrowingTransactionRequest[]>(
  'selectedData',
  {
    required: true,
  },
);

const props = defineProps<{
  type?: 'History';
  search?: string;
  filters: BorrowingRequestFilterParams;
  fetchFunction: (
    params: Omit<BorrowingRequestFetchParams, 'prelist'>,
  ) => Promise<FetchResponse | undefined>;
  data: BorrowingTransactionRequest[];
  isBorrowed?: boolean;
  borrower?: boolean;
}>();

const { manager } = getTransactionRole('borrowingRole');

const showCancelBorrowingTransaction = ref<boolean>(false);
const showCancelExtensionRequest = ref<boolean>(false);
const showDeclineExtensionRequest = ref<boolean>(false);
const showReportDamage = ref<boolean>(false);
const showReportMissing = ref<boolean>(false);
const showBorrowingLog = ref<boolean>(false);
const showAssetConfirmation = ref<boolean>(false);
const showCancelReport = ref<boolean>(false);

const singleOptions = computed<MenuOption[]>(() => {
  const options: ExtendedMenuOption[] = borrowDetailOptions(
    selectedDatum,
    router,
    {
      showReportDamage,
      showReportMissing,
      showCancelBorrowingTransaction,
      showCancelExtensionRequest,
      showDeclineExtensionRequest,
      showBorrowingLog,
      showAssetConfirmation,
      showCancelReport,
    },
  );

  return options.filter((option) => {
    if (!option.for) return option;

    return option.for.includes(selectedDatum.value?.status ?? '');
  });
});

const childrenSingleOptions = computed<MenuOption[]>(() => {
  const options: ExtendedMenuOption[] = borrowDetailChildrenOptions(
    selectedDatum,
    {
      showReportDamage,
      showReportMissing,
      showBorrowingLog,
    },
  );

  return options.filter((option) => {
    if (!option.for) return option;

    return option.for.includes(selectedDatum.value?.status ?? '');
  });
});

const selectedDatum = ref<BorrowingTransactionRequest>();

const columns = detailColumns(props.isBorrowed);

const childrenColumns = childColumns();

const isHasLinkedAsset = computed<boolean>(() => {
  return props.data.some((d) => d.children && d.children.length > 0);
});
</script>
<template>
  <div data-ts-name="borrow-detail-table">
    <CancelReport
      v-if="manager"
      v-model:visible="showCancelReport"
      :selected-data="selectedDatum ? [selectedDatum] : []"
      table-name="borrow-detail-table"
    />
    <AssetConfirmation
      v-if="manager"
      v-model:visible="showAssetConfirmation"
      :selected-datum
    />
    <BorrowingLog
      v-model:visible="showBorrowingLog"
      :data="
        data.flatMap((datum) =>
          datum.children && datum.children.length > 0
            ? [datum, ...datum.children]
            : datum,
        ) as BorrowingTransactionRequest[]
      "
      :selected-datum
    />
    <TSDialogReportDamage
      :id="selectedDatum.asset"
      v-if="selectedDatum && manager"
      v-model:visible="showReportDamage"
      :by-user="false"
      @reported="incrementTableKey"
    />
    <TSDialogReportMissing
      :id="selectedDatum.asset"
      v-if="selectedDatum && manager"
      v-model:visible="showReportMissing"
      @reported="incrementTableKey"
    />
    <DeclineExtensionRequest
      v-if="manager"
      v-model:visible="showDeclineExtensionRequest"
      :selected-data="selectedDatum ? [selectedDatum] : []"
    />
    <CancelExtensionRequest
      v-if="manager"
      v-model:visible="showCancelExtensionRequest"
      :selected-data="selectedDatum ? [selectedDatum] : []"
    />
    <CancelBorrowingTransaction
      v-if="manager"
      v-model:visible="showCancelBorrowingTransaction"
      :selected-data="selectedDatum ? [selectedDatum] : []"
    />
    <TSDataTable
      :key="tableKey"
      v-if="type !== 'History' && manager"
      v-model:selected-data="selectedData"
      :children-columns="isHasLinkedAsset ? childrenColumns : undefined"
      :children-options="childrenSingleOptions"
      :children-use-option="!props.borrower"
      :columns
      :fetch-function
      :filters
      :options="singleOptions"
      :rows="10"
      :search
      :use-option="!props.borrower"
      @toggle-children-option="selectedDatum = $event"
      @toggle-option="selectedDatum = $event"
      data-key="_id"
      lazy
      table-name="borrow-detail-table"
      use-paginator
    />
    <TSDataTable
      :key="tableKey + 1"
      v-else
      :children-columns="isHasLinkedAsset ? childrenColumns : undefined"
      :children-use-option="!props.borrower"
      :columns
      :fetch-function
      :filters
      :options="props.borrower ? undefined : singleOptions"
      :rows="10"
      :search
      :use-option="!props.borrower"
      @toggle-children-option="selectedDatum = $event"
      @toggle-option="selectedDatum = $event"
      data-key="_id"
      lazy
      selection-type="single"
      table-name="borrow-detail-table"
      use-paginator
    />
  </div>
</template>
